import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Grid,
  Link,
  Theme,
  Typography,
  useTheme,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import supportTrack from 'src/support/handlers/track';

const faqQuestionAndAnswer = [
  {
    question: 'Is QuillBot’s Citation Generator free?',
    answer: (
      <Typography>
        Yes! Our online Citation Generator is free to use. In fact, we love freebies, so if you
        haven’t checked out our other free writing and research tools yet, you’re missing out.
      </Typography>
    ),
  },
  {
    question: 'Can I export my list of full citations?',
    answer: (
      <Typography>
        You have the option of copying your entire list of full citations to your clipboard or
        exporting them to Word. You can also download them in BibTeX or CSL-JSON formats.
      </Typography>
    ),
  },
  {
    question: 'Do I have to create an account?',
    answer: (
      <Typography>
        Accounts are cool and all, but sometimes you need to work and don’t have time to make up a
        new password. You can use the QuillBot platform tools, including the Citation Generator, for
        free without even signing in. You may eventually want an account for the perks of higher
        word counts and other features within the platform, but you can decide that for yourself
        whenever you’re ready.
      </Typography>
    ),
  },
  {
    question: 'What styles does the QuillBot Citation Generator support?',
    answer: (
      <Typography>
        The citation generator supports APA, MLA, and Chicago style (Author-Date) formats for citing
        books and websites, but we are always working to add more variety to our tools. Which ones
        do you want us to add first? Let us know&nbsp;
        <Link href="/contact" target="_blank">
          <a>here</a>
        </Link>
        &nbsp; in our feedback portal.
      </Typography>
    ),
    containsLink: true,
  },
  {
    question: 'Are citations and references the same?',
    answer: (
      <>
        <div>
          <Typography>
            No. A citation can be either full or in-text and notates a source that the writer has
            used to support their argument or research. There are many styles of citations, but the
            most commonly used styles are APA, MLA, and Chicago citation styles. A citation says,
            “Hey, I didn’t know this before I read/spoke to this person, so I’m noting where the
            idea or information came from originally.”
          </Typography>
        </div>

        <div>
          <Typography>
            Without context, a reference functions the same as a full citation. It refers to work
            done by other authors to support your own work. However, “References” can also refer to
            a specific page at the end of your work which lists all of the sources cited to support
            the work, IF you are working in APA format or the Author-Date system in Chicago style.
          </Typography>
        </div>

        <div>
          <Typography>
            In MLA format, this page would be called a Works Cited, although those working in
            Author-Date Chicago style have the option to title their list of full citations either
            “References” or “Works Cited”. A Bibliography page is only used within the Notes and
            Bibliography system for Chicago style citations, just as an FYI.
          </Typography>
        </div>
      </>
    ),
  },
  {
    question: 'Is a bibliography the same thing as a citation?',
    answer: (
      <>
        <div>
          <Typography>
            No. A citation can be either full or in-text and notates a source that the writer has
            used to support their argument or research. There are many styles of citations, but the
            most commonly used styles are the APA, MLA, and Chicago citation styles. A citation
            says, “Hey, I didn’t know this before I read/spoke to this person, so I’m noting where
            the idea or information came from originally.”
          </Typography>
        </div>

        <div>
          <Typography>
            A bibliography, on the other hand, is a complete list of sources used within a work to
            support your claims, ideas, and arguments. A Bibliography page, when specifically titled
            this way, is used in the Notes and Bibliography system for Chicago style citations. A
            single citation could never be mistaken for bibliography, which by definition, is a list
            of citations in a specific style.
          </Typography>
        </div>
      </>
    ),
  },
];

const useFaqSectionStyles = makeStyles<Theme, { hoverColor?: string }>((theme) => ({
  faqholder: {
    paddingTop: '2px',
    // backgroundColor: theme.palette.grey[800],
    backgroundColor: 'transparent',
    '&:last-child': {
      '& $expPanel': {
        borderWidth: 0,
      },
      '& $root:hover': {
        mixBlendMode: 'multiply',
      },
    },
  },
  expanded: {
    color: theme.palette.primary.main,
    // backgroundColor: '#ffffff',
  },
  expPanel: {
    border: '1px solid',
    borderWidth: '1px 0px',
    borderColor: theme.palette.divider,
    // backgroundColor: theme.palette.grey[800],
    backgroundColor: 'transparent',
    // theme.palette.background.default,
    boxShadow: 'none',
    marginBottom: '0px !important',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 0,
    },
  },
  expDetail: {
    paddingLeft: '8px',
    paddingBottom: '32px',
    '& div': {
      marginBottom: '10px',
    },
  },
  heading: {
    fontWeight: 600,
  },
  root: {
    // width: "100%",
    fontWeight: 600,
    minHeight: '70px',
    // color: '#4F4F4F',
    padding: '8px',
    color: '#484B4E',
    boxShadow: 'none',
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: ({ hoverColor }) => hoverColor || theme.palette.grey[800],
    },

    [theme.breakpoints.down(767)]: {
      fontSize: '15px',
    },
  },
}));

export const FaqSectionAccorion = ({
  question,
  questionComponent,
  answer,
  onChange,
  hoverColor,
}: {
  question: string;
  questionComponent?: keyof JSX.IntrinsicElements;
  answer: string | JSX.Element | JSX.Element[];
  onChange?: AccordionProps['onChange'];
  hoverColor?: string;
}) => {
  // const { question, answer } = props;
  const classes = useFaqSectionStyles({ hoverColor });

  return (
    <Grid item xs={12} className={`${classes.faqholder}`}>
      <Accordion
        classes={{ root: classes.expPanel }}
        style={{ width: '100%', borderTop: '0' }}
        // onChange={(event, expanded) => {
        //   if (expanded) supportTrack.faqOpened('Citation-generator FAQ', question);
        // }}
        onChange={onChange}
      >
        <AccordionSummary
          classes={{ root: classes.root, expanded: classes.expanded }}
          className="qbp-semibold"
          aria-controls="panel1bh-content"
        >
          <Typography
            className={classes.heading}
            display="block"
            component={questionComponent as React.ElementType}
          >
            {question}
          </Typography>
        </AccordionSummary>

        <AccordionDetails classes={{ root: classes.expDetail }} style={{ flexDirection: 'column' }}>
          {answer}
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

const CitationFaqSection = () => {
  const theme = useTheme();
  const onChange =
    (question: string): AccordionProps['onChange'] =>
    (event, expanded) => {
      if (expanded) supportTrack.faqOpened('Citation-generator FAQ', question);
    };

  return (
    <Grid
      container
      style={{
        // marginTop: 80,
        // padding: isSmallScreen ? '60px 20px' : '60px 100px',
        padding: '56px 16px',
        // backgroundColor: theme.palette.grey[800],
        backgroundColor: 'transparent',
        backgroundImage: 'url("/images/coWriter/cg-creative-3.svg")',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: '90% 100%',
      }}
      alignItems="center"
      justifyContent="center"
    >
      <Grid container style={{ maxWidth: '1028px' }}>
        <Grid item>
          <Typography
            component="h4"
            style={{
              fontSize: 20,
              fontWeight: 600,
              paddingLeft: 8,
            }}
          >
            FAQs
          </Typography>
        </Grid>

        {faqQuestionAndAnswer.map((questionAndAnswer) => (
          <FaqSectionAccorion
            {...questionAndAnswer}
            key={questionAndAnswer.question}
            onChange={onChange(questionAndAnswer.question)}
            hoverColor={theme.specialColors.CG_INFO_BG}
          />
        ))}
      </Grid>
    </Grid>
  );
};

export default CitationFaqSection;
